import { useMemo, useRef, useState } from "react";
import { LockOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Tag, message } from "antd";

import PageHeader from "@/components/UI/PageHeader";
import { useSelectableTableView, useTableHeight } from "@/utils/tableHelpers";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useFetch, is403ApiError } from "@/hooks/FetchHook";
import useTableState from "@/hooks/useTableState";
import { createDefaultTableState } from "@/utils/workspaceState";
import { ColumnVisibility, ColumnsType } from "@/types/tableState.types";
import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import AppliedFiltersSection from "@/components/UI/TableToolbar/Filtering/AppliedFiltersSection";
import {
  constructQueryString,
  prepareUnifiedDataPostRequestBody,
} from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { BASE_ENDPOINT } from "@/components/Templates/constants";
import PaginatedTable from "@/components/UI/PaginatedTable";
import ErrorComponent from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import Loader from "@/components/UI/Loader";

import { useUser } from "@/contexts/UserContext";
import { getUserRoleForTrial } from "@/components/Insights/insights.utils";

import { EnrollmentTargetCurve } from "@/components/PlanOfRecord/EnrollmentTargetCurve";
import { EnrollmentTargetModal } from "@/components/TrialEnrollment/EnrollmentTargetModal";
import {
  can,
  hasPlanEditorRole,
  type PlanUser,
} from "@/views/PlanOfRecord/planAccessData";

import {
  ENROLLMENT_TARGETS_DATASET_TYPE,
  buildUpsertPayload,
  computeDecreasingMonths,
  mapTargetRowToTrackedTarget,
} from "./enrollmentTargetMapping";
import { createEnrollmentTargetColumns } from "./enrollmentTargetsTableConfig";
import type {
  EnrollmentTargetData,
  EnrollmentTargetMetadata,
  EnrollmentTargetRow,
} from "./enrollmentTarget.types";
import { useEnrollmentTargetMutations } from "@/hooks/useEnrollmentTargetMutations";

/** A trial's whole enrolment plan comfortably fits on one page. */
const FULL_LIST_PAGE_SIZE = 200;

/**
 * The sponsor's planned enrolment, on the Enrollment screen rather than in the plan of
 * record.
 *
 * The targets are plan data — sponsor-authored and gated on the same plan_editor role — but
 * enrolment is where they are read, next to actuals and the vendor's curve on the sibling
 * Expectation vs Actual tab.
 *
 * Only the sponsor's series appears here: actuals come from the core data model and the
 * vendor's curve from ingestion, and both are already plotted against plan next door.
 */
export default function PlannedEnrolmentTab() {
  const queryString = constructQueryString({
    baseParams: { dataset_type: ENROLLMENT_TARGETS_DATASET_TYPE },
  });

  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useFetch({ endpointSuffix: `filters/${queryString}` });

  if (metadataError) {
    return (
      <div className="flex mt-25 justify-center items-center">
        {is403ApiError(metadataError) ? <Error403 /> : <ErrorComponent />}
      </div>
    );
  }

  if (metadataLoading || !metadata) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <PlannedEnrolmentContent metadata={metadata as EnrollmentTargetMetadata} />
  );
}

function PlannedEnrolmentContent({
  metadata,
}: {
  metadata: EnrollmentTargetMetadata;
}) {
  const { id } = useScopeParams();
  const [modalOpen, setModalOpen] = useState(false);

  const { userInfo } = useUser();
  const grantedRole = getUserRoleForTrial(userInfo, id);
  const currentUser: PlanUser = useMemo(
    () => ({
      id: userInfo?.id ?? "",
      name: userInfo
        ? `${userInfo.first_name} ${userInfo.last_name}`.trim() ||
          userInfo.email
        : "",
      jobTitle: "",
      roleId: hasPlanEditorRole(grantedRole) ? "plan-editor" : "plan-viewer",
      grantedVia: "",
      grantedAt: "",
    }),
    [userInfo, grantedRole],
  );
  const canEdit = can(currentUser, "plan.edit");

  const { upsertTarget } = useEnrollmentTargetMutations();

  const pageRef = useRef<HTMLDivElement>(null);
  const { tableHeight: maxMenuHeight } = useTableHeight({ tableRef: pageRef });

  const metadataColumns = useMemo(
    () => metadata.columns.map((column) => column.field),
    [metadata.columns],
  );
  const defaultHiddenColumns = useMemo(
    () =>
      metadata.columns
        .filter((column) => Boolean(column.hidden))
        .map((column) => column.field),
    [metadata.columns],
  );
  const metadataFilterKeys = useMemo(
    () => Object.keys(metadata.filters || {}),
    [metadata.filters],
  );

  const initialTableState = useMemo(() => {
    const base = createDefaultTableState(metadataColumns, defaultHiddenColumns);
    return {
      ...base,
      pagination: { ...base.pagination, total: metadata.row_count },
    };
  }, [metadataColumns, defaultHiddenColumns, metadata.row_count]);

  const { currentTableState, tableStateFunctions } = useTableState({
    initialState: initialTableState,
    pageName: ENROLLMENT_TARGETS_DATASET_TYPE,
    isSingleSubject: false,
    trialId: id,
    metadataColumns,
    metadataFilterKeys,
  });

  const allFiltersData = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(metadata.filters || {}).map(([key, filterInfo]) => [
          key,
          {
            data_type: filterInfo.data_type || "text",
            values: filterInfo.values || [],
          },
        ]),
      ),
    [metadata.filters],
  );

  const payload = useMemo(
    () =>
      prepareUnifiedDataPostRequestBody({
        global_filters: currentTableState.filters,
        allFiltersData,
        page: currentTableState.pagination.current,
        pageSize: currentTableState.pagination.pageSize,
        sorting: currentTableState.sorting,
      }),
    [
      currentTableState.filters,
      currentTableState.pagination,
      currentTableState.sorting,
      allFiltersData,
    ],
  );

  const {
    data: targetDataRaw,
    isLoading: loadingTargetData,
    error: targetDataError,
  } = useFetch({
    endpointSuffix: `${BASE_ENDPOINT}/`,
    method: "POST",
    body: { dataset_type: ENROLLMENT_TARGETS_DATASET_TYPE, ...payload },
    callback: (data) => {
      if (currentTableState.pagination.total !== data.count) {
        tableStateFunctions.setPagination({
          ...currentTableState.pagination,
          total: data.count,
        });
      }
    },
  });
  const targetData = targetDataRaw as EnrollmentTargetData | undefined;

  // The chart and the "cumulative total can't fall" check need every month, not just the
  // current page — a table screen small enough for one page, so one extra fetch at a
  // generous size covers it without a second dataset endpoint.
  const fullListPayload = useMemo(
    () =>
      prepareUnifiedDataPostRequestBody({
        global_filters: {},
        allFiltersData,
        page: 1,
        pageSize: FULL_LIST_PAGE_SIZE,
        sorting: [{ field: "month", direction: "ascending" }],
      }),
    [allFiltersData],
  );
  const { data: fullTargetDataRaw } = useFetch({
    endpointSuffix: `${BASE_ENDPOINT}/`,
    method: "POST",
    body: { dataset_type: ENROLLMENT_TARGETS_DATASET_TYPE, ...fullListPayload },
  });
  const allTargetRows = useMemo(
    () =>
      (fullTargetDataRaw as EnrollmentTargetData | undefined)?.results ?? [],
    [fullTargetDataRaw],
  );
  const allTargets = useMemo(
    () => allTargetRows.map(mapTargetRowToTrackedTarget),
    [allTargetRows],
  );
  const decreasingMonths = useMemo(
    () => computeDecreasingMonths(allTargets),
    [allTargets],
  );

  const { selectedRecord, setSelectedRecord, getClickableRowProps } =
    useSelectableTableView<EnrollmentTargetRow>();
  const editingTarget = selectedRecord
    ? mapTargetRowToTrackedTarget(selectedRecord)
    : null;

  const columns = useMemo(
    () =>
      createEnrollmentTargetColumns(
        currentTableState.columns
          .filter(
            (column: ColumnsType) =>
              column.visibility === ColumnVisibility.VISIBLE,
          )
          .map((column: ColumnsType) => column.fieldName),
        currentTableState.lockedColumns,
        decreasingMonths,
      ),
    [
      currentTableState.columns,
      currentTableState.lockedColumns,
      decreasingMonths,
    ],
  );

  return (
    <div ref={pageRef} className="flex w-full min-w-0 flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <PageHeader
            title="Enrollment - Planned"
            tooltipText="The sponsor's own enrolment plan. Expectation vs Actual compares it against actuals and the vendor's projection."
          />
          {!canEdit ? (
            <Tag icon={<LockOutlined />} color="default">
              Read only
            </Tag>
          ) : null}
        </div>

        {canEdit ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedRecord(null);
              setModalOpen(true);
            }}
          >
            Add target
          </Button>
        ) : null}
      </div>

      {decreasingMonths.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={`${decreasingMonths.length} month${decreasingMonths.length === 1 ? "" : "s"} fall below the month before`}
          description={`A cumulative total cannot decrease. Check ${decreasingMonths.join(", ")}.`}
        />
      ) : null}

      <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
        <h3 className="mb-2 text-[14px] font-semibold text-[#1F2933]">
          Planned enrolment
        </h3>
        <EnrollmentTargetCurve
          data={allTargets}
          totalTarget={
            allTargets.length
              ? Math.max(...allTargets.map((target) => target.sponsorPlan))
              : 0
          }
        />
      </div>

      <TableToolbar
        currentTableState={currentTableState}
        tableStateFunctions={tableStateFunctions}
        tableMetadata={metadata}
        maxMenuHeight={maxMenuHeight}
        fullPageName={ENROLLMENT_TARGETS_DATASET_TYPE}
        endpointSuffix={BASE_ENDPOINT}
        name={ENROLLMENT_TARGETS_DATASET_TYPE}
        datasetName={ENROLLMENT_TARGETS_DATASET_TYPE}
      />

      <AppliedFiltersSection
        filters={currentTableState.filters}
        clearFilters={() => tableStateFunctions.setFilters({})}
        setFilters={tableStateFunctions.setFilters}
        filterOptions={metadata.filters}
        allFiltersData={metadata.filters}
      />

      {targetDataError ? (
        <ErrorComponent />
      ) : (
        <PaginatedTable
          data={targetData?.results}
          columns={columns}
          loading={loadingTargetData}
          setPagination={tableStateFunctions.setPagination}
          pagination={currentTableState.pagination}
          rowKey="id"
          onRow={canEdit ? getClickableRowProps : undefined}
        />
      )}

      <EnrollmentTargetModal
        open={modalOpen || selectedRecord !== null}
        editing={editingTarget}
        targets={allTargets}
        actorName={currentUser.name}
        onCancel={() => {
          setModalOpen(false);
          setSelectedRecord(null);
        }}
        onSubmit={async (draft) => {
          try {
            await upsertTarget(buildUpsertPayload(draft));
            setModalOpen(false);
            setSelectedRecord(null);
            message.success(`${draft.month} set to ${draft.sponsorPlan}.`);
          } catch {
            message.error("Couldn't save the target. Try again.");
          }
        }}
      />
    </div>
  );
}
